import { useState } from 'react'
import { X, TrendingUp, TrendingDown, Target, Activity, DollarSign, BarChart3 } from 'lucide-react'
import { formatCurrency, formatPercent, formatDate } from '../lib/utils'
import { cn } from '../lib/utils'

interface Backtest {
  id: string
  strategy: { name: string }
  startDate: string
  endDate: string
  initialCapital: number
  finalCapital: number
  totalReturn: number
  totalTrades: number
  winningTrades: number
  losingTrades: number
  winRate: number
  sharpeRatio: number | null
  maxDrawdown: number
  profitFactor: number | null
  avgTradeReturn?: number | null
  trades?: any[]
  equityCurve?: any[]
  status: string
  createdAt: string
  completedAt: string | null
}

interface BacktestDetailModalProps {
  backtest: Backtest
  onClose: () => void
}

export function BacktestDetailModal({ backtest, onClose }: BacktestDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'trades' | 'equity'>('overview')

  const isProfitable = backtest.totalReturn >= 0
  const totalPnl = backtest.finalCapital - backtest.initialCapital

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{backtest.strategy.name}</h2>
            <p className="text-sm text-gray-500">
              {formatDate(backtest.startDate)} - {formatDate(backtest.endDate)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className={cn(
              'rounded-xl p-4',
              isProfitable ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            )}>
              <div className="flex items-center space-x-2 mb-2">
                {isProfitable ? (
                  <TrendingUp className="w-4 h-4 text-green-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm text-gray-600">Total Return</span>
              </div>
              <p className={cn(
                'text-2xl font-bold',
                isProfitable ? 'text-green-600' : 'text-red-600'
              )}>
                {isProfitable ? '+' : ''}{formatPercent(backtest.totalReturn / 100)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-600">Total Trades</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{backtest.totalTrades}</p>
              <p className="text-sm text-gray-500 mt-1">
                {backtest.winningTrades} wins / {backtest.losingTrades} losses
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-gray-600">Win Rate</span>
              </div>
              <p className={cn(
                'text-2xl font-bold',
                backtest.winRate >= 50 ? 'text-green-600' : 'text-red-600'
              )}>
                {backtest.winRate.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {backtest.totalTrades > 0 
                  ? `${backtest.winningTrades}/${backtest.totalTrades} trades`
                  : 'No trades'}
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-gray-600">Max Drawdown</span>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {formatPercent(backtest.maxDrawdown / 100)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Peak to trough</p>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Sharpe Ratio</p>
              <p className="text-lg font-semibold text-gray-900">
                {backtest.sharpeRatio?.toFixed(2) || 'N/A'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Profit Factor</p>
              <p className="text-lg font-semibold text-gray-900">
                {backtest.profitFactor?.toFixed(2) || 'N/A'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Avg Trade Return</p>
              <p className={cn(
                'text-lg font-semibold',
                (backtest.avgTradeReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              )}>
                {backtest.avgTradeReturn 
                  ? `${backtest.avgTradeReturn >= 0 ? '+' : ''}${(backtest.avgTradeReturn * 100).toFixed(2)}%`
                  : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Initial Capital</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(backtest.initialCapital)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Final Capital</p>
              <p className={cn(
                'text-lg font-semibold',
                backtest.finalCapital >= backtest.initialCapital ? 'text-green-600' : 'text-red-600'
              )}>
                {formatCurrency(backtest.finalCapital)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-lg font-semibold text-gray-900">
                {backtest.completedAt 
                  ? formatDate(backtest.completedAt)
                  : 'In Progress'}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <div className="flex space-x-6">
              {(['overview', 'trades', 'equity'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'pb-3 text-sm font-medium border-b-2 transition-colors capitalize',
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[200px]">
            {activeTab === 'trades' && backtest.trades && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Date</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Market</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Side</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Size</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">P&L</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Return</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {backtest.trades.slice(0, 20).map((trade: any, idx: number) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-gray-600">{formatDate(trade.date)}</td>
                        <td className="px-4 py-2">{trade.market}</td>
                        <td className="px-4 py-2">
                          <span className={cn(
                            'font-medium',
                            trade.side === 'YES' ? 'text-green-600' : 'text-red-600'
                          )}>
                            {trade.side}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">{formatCurrency(trade.size)}</td>
                        <td className={cn(
                          'px-4 py-2 text-right font-medium',
                          trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl)}
                        </td>
                        <td className={cn(
                          'px-4 py-2 text-right',
                          trade.returnPct >= 0 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {trade.returnPct >= 0 ? '+' : ''}{(trade.returnPct * 100).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {backtest.trades.length > 20 && (
                  <p className="text-center text-sm text-gray-500 py-4">
                    +{backtest.trades.length - 20} more trades
                  </p>
                )}
              </div>
            )}

            {activeTab === 'equity' && backtest.equityCurve && (
              <div className="space-y-4">
                <div className="h-64 bg-gray-50 rounded-lg p-4 flex items-end">
                  {< EquityChart data={backtest.equityCurve} initialCapital={backtest.initialCapital} />}
                </div>
                <p className="text-sm text-gray-500 text-center">
                  Equity curve over backtest period
                </p>
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Select "Trades" or "Equity" tab to see detailed results</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Simple equity chart visualization
function EquityChart({ data, initialCapital }: { data: any[]; initialCapital: number }) {
  if (!data || data.length === 0) return null

  const values = data.map((d) => d.equity)
  const min = Math.min(...values, initialCapital)
  const max = Math.max(...values)
  const range = max - min || 1

  return (
    <div className="w-full h-full flex items-end space-x-1">
      {data.map((point, idx) => {
        const height = ((point.equity - min) / range) * 100
        const isProfitable = point.equity >= initialCapital
        
        return (
          <div
            key={idx}
            className="flex-1 flex flex-col justify-end"
          >
            <div
              className={cn(
                'w-full rounded-t',
                isProfitable ? 'bg-green-400' : 'bg-red-400'
              )}
              style={{ height: `${Math.max(height, 5)}%` }}
              title={`${formatDate(point.date)}: ${formatCurrency(point.equity)}`}
            />
          </div>
        )
      })}
    </div>
  )
}
